/**
 * Forecast configuration API methods
 */

import { getSession } from '../../../../../core/auth/supabase-auth';
import { getApiBaseUrl } from '../core';
import { TrainingApiResponse } from '../types';

export interface SaveForecastConfigPayload {
  features: Array<{
    id: string;
    data_source?: string;
    api_source?: string;
    fcst_var?: string;
    latitude?: number;
    longitude?: number;
    feature_index?: number;
  }>;
  zeitzone?: string;
}

export interface ApiParameter {
  id: string;
  api_source: string;
  parameter: string;
  unit: string;
  name_user: string;
  lbl: string;
}

export async function saveForecastConfig(
  sessionId: string,
  payload: SaveForecastConfigPayload
): Promise<TrainingApiResponse> {
  const session = await getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(`${getApiBaseUrl()}/save-forecast-config/${sessionId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify(payload)
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to save forecast config');
  return data;
}

export async function getForecastConfig(
  sessionId: string
): Promise<TrainingApiResponse> {
  const session = await getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const response = await fetch(`${getApiBaseUrl()}/forecast-config/${sessionId}`, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to get forecast config');
  return data;
}

export async function getApiParameters(
  apiSource?: string
): Promise<TrainingApiResponse<{ parameters: ApiParameter[] }>> {
  const session = await getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const url = apiSource
    ? `${getApiBaseUrl()}/api-parameters?api_source=${encodeURIComponent(apiSource)}`
    : `${getApiBaseUrl()}/api-parameters`;

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    }
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to get API parameters');
  return data;
}
